import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtError, eur, NIVEAU_STYLES } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { MapPin, Camera, Plus, FileText, ShieldAlert, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function SiteDetail() {
  const { id } = useParams();
  const [site, setSite] = useState(null);
  const [risk, setRisk] = useState(null);
  const [j, setJ] = useState({ action: "", commentaire: "", heures: "", incident: false });

  const load = () => api.get(`/sites/${id}`).then((r) => {
    setSite(r.data);
    if (r.data.territoire) api.get(`/risks/profile/${encodeURIComponent(r.data.territoire)}`).then((x) => setRisk(x.data)).catch(() => {});
  }).catch((e) => toast.error(fmtError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addJournal = async (e) => {
    e.preventDefault();
    const send = async (coords = {}) => {
      try {
        await api.post(`/sites/${id}/journal`, { ...j, heures: parseFloat(j.heures) || 0, ...coords });
        toast.success("Entrée ajoutée au journal");
        setJ({ action: "", commentaire: "", heures: "", incident: false });
        load();
      } catch (err) { toast.error(fmtError(err)); }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => send({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => send());
    } else send();
  };

  if (!site) return <p className="text-muted-foreground" data-testid="site-loading">Chargement…</p>;

  return (
    <div className="space-y-5" data-testid="site-detail">
      <div>
        <Link to="/chantiers" className="text-sm font-bold text-amber-600">← Chantiers</Link>
        <div className="flex items-start justify-between mt-1">
          <div>
            <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">{site.nom}</h1>
            <p className="text-sm text-muted-foreground font-mono-num">{site.numero} — {site.client_nom || "Sans client"}</p>
          </div>
          <span className="bg-amber-500 text-slate-900 text-xs font-extrabold px-3 py-1.5 rounded-lg uppercase">{site.statut}</span>
        </div>
        <p className="text-sm mt-1 flex items-center gap-1 text-muted-foreground">
          <MapPin className="h-4 w-4" /> {site.adresse || "—"}, {site.commune} ({site.territoire})
          {site.latitude && (
            <a data-testid="site-open-nav" className="ml-2 inline-flex items-center gap-1 text-amber-600 font-bold"
              href={`https://www.openstreetmap.org/?mlat=${site.latitude}&mlon=${site.longitude}#map=17/${site.latitude}/${site.longitude}`} target="_blank" rel="noreferrer">
              Ouvrir navigation <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </p>
      </div>

      <Tabs defaultValue="journal">
        <TabsList className="grid grid-cols-4 w-full max-w-xl">
          <TabsTrigger value="journal" data-testid="tab-journal">Journal</TabsTrigger>
          <TabsTrigger value="calculs" data-testid="tab-calculs">Calculs</TabsTrigger>
          <TabsTrigger value="devis" data-testid="tab-devis">Devis</TabsTrigger>
          <TabsTrigger value="risques" data-testid="tab-risques">Risques</TabsTrigger>
        </TabsList>

        <TabsContent value="journal" className="space-y-4">
          <form onSubmit={addJournal} className="bg-card rounded-xl border border-border p-4 space-y-3" data-testid="journal-form">
            <Input data-testid="journal-action" required placeholder="Action réalisée *" value={j.action} onChange={(e) => setJ({ ...j, action: e.target.value })} className="py-3.5" />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="journal-heures" type="number" step="any" placeholder="Heures" value={j.heures} onChange={(e) => setJ({ ...j, heures: e.target.value })} />
              <label className="flex items-center gap-2 text-sm font-semibold">
                <input type="checkbox" data-testid="journal-incident" checked={j.incident} onChange={(e) => setJ({ ...j, incident: e.target.checked })} /> Incident
              </label>
            </div>
            <Input data-testid="journal-commentaire" placeholder="Commentaire" value={j.commentaire} onChange={(e) => setJ({ ...j, commentaire: e.target.value })} />
            <Button data-testid="journal-submit" type="submit" className="w-full h-13 bg-amber-500 text-slate-900 font-bold hover:bg-amber-400">
              <Camera className="h-4 w-4 mr-2" /> AJOUTER AU JOURNAL
            </Button>
          </form>
          <div className="space-y-2" data-testid="journal-list">
            {[...(site.journal || [])].reverse().map((e) => (
              <div key={e.id} className={`bg-card rounded-xl border p-3 ${e.incident ? "border-red-400" : "border-border"}`} data-testid={`journal-entry-${e.id}`}>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span className="font-bold">{e.personne}</span>
                  <span className="font-mono-num">{new Date(e.date).toLocaleString("fr-FR")}</span>
                </div>
                <div className="font-semibold mt-1">{e.action} {e.incident && <span className="text-red-600 text-xs font-bold ml-1">INCIDENT</span>}</div>
                {e.commentaire && <div className="text-sm text-muted-foreground">{e.commentaire}</div>}
                {e.heures > 0 && <div className="text-xs font-mono-num mt-1">{e.heures} h</div>}
              </div>
            ))}
            {(site.journal || []).length === 0 && <p className="text-sm text-muted-foreground">Journal vide.</p>}
          </div>
        </TabsContent>

        <TabsContent value="calculs">
          <div className="space-y-2" data-testid="site-calcs-list">
            {(site.calculations || []).map((c) => (
              <div key={c.id} className={`rounded-xl border-2 p-3 ${NIVEAU_STYLES[c.result?.niveau] || ""}`}>
                <div className="font-bold text-sm">{c.result?.label}</div>
                <div className="font-mono-num font-bold">{c.result?.quantite_recommandee} {c.result?.unite}
                  {c.result?.cout_total != null && <span className="ml-3">{eur(c.result.cout_total)}</span>}
                </div>
                <div className="text-xs opacity-70">{new Date(c.created_at).toLocaleString("fr-FR")}</div>
              </div>
            ))}
            {(site.calculations || []).length === 0 && (
              <p className="text-sm text-muted-foreground">Aucun calcul enregistré. <Link to="/calculs" className="text-amber-600 font-bold">Lancer un calcul</Link></p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="devis">
          <div className="space-y-2" data-testid="site-quotes-list">
            {(site.quotes || []).map((q) => (
              <Link key={q.id} to={`/devis/${q.id}`} className="block bg-card rounded-xl border border-border p-3 hover:border-amber-500">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-sm">{q.numero} — {q.objet}</span>
                  <span className="font-mono-num font-bold">{eur(q.total_ttc)}</span>
                </div>
                <div className="text-xs text-muted-foreground">{q.statut} — {q.date}</div>
              </Link>
            ))}
            {(site.quotes || []).length === 0 && (
              <p className="text-sm text-muted-foreground">Aucun devis lié. <Link to={`/devis?new=1&site=${id}`} className="text-amber-600 font-bold">Créer un devis</Link></p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="risques">
          {risk ? (
            <div className="space-y-3" data-testid="site-risks">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-card rounded-xl border border-border p-4">
                  <div className="text-xs font-bold uppercase text-muted-foreground">Zone sismique</div>
                  <div className={`font-display text-4xl font-extrabold ${risk.zone_sismique >= 5 ? "text-red-600" : "text-emerald-600"}`}>{risk.zone_sismique ?? "—"}</div>
                  {risk.microzonage_possible && <div className="text-xs mt-1 text-amber-600 font-semibold">Microzonage communal possible — vérifier Géorisques</div>}
                </div>
                <div className="bg-card rounded-xl border border-border p-4">
                  <div className="text-xs font-bold uppercase text-muted-foreground">Vents cycloniques</div>
                  <div className={`font-display text-4xl font-extrabold ${risk.vents_cycloniques ? "text-red-600" : "text-emerald-600"}`}>{risk.vents_cycloniques ? "OUI" : "NON"}</div>
                  {risk.vents_cycloniques && <div className="text-xs mt-1 text-amber-600 font-semibold">Exigences 2024 — validation BET requise</div>}
                </div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-400 rounded-xl p-3 text-sm font-semibold flex gap-2">
                <ShieldAlert className="h-5 w-5 shrink-0 text-amber-600" /> {risk.avertissement}
              </div>
              <div className="bg-card rounded-xl border border-border p-4">
                <div className="font-bold text-sm mb-2 uppercase">Checklist climat tropical</div>
                <ul className="space-y-1.5">
                  {risk.checklist_tropicale.map((c, i) => (
                    <li key={i} className="text-sm flex gap-2"><span className="text-amber-600 font-bold">▸</span>{c}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : <p className="text-sm text-muted-foreground">Profil risques indisponible.</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
